import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { queuedTaskIds } from "@/lib/offlineQueue";
import { useAuth } from "@/context/AuthContext";
import { SectionTitle } from "@/components/common";
import { Button } from "@/components/ui/button";
import { ScanLine, Plus } from "lucide-react";
import { toast } from "sonner";
import { TaskCard } from "@/components/tasks/TaskCard";
import { DoneTaskRow } from "@/components/tasks/DoneTaskRow";
import { ScanDialogs, TaskDialogs } from "@/components/tasks/TaskDialogs";
import { WorkflowCompleteDialog, WORKFLOW_TASK_TYPES } from "@/components/tasks/WorkflowCompleteDialog";
import { TaskEditDialog, TaskHistoryDialog, TaskReasonDialog } from "@/components/tasks/TaskAdminActions";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Tasks() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isWorker = user?.role === "worker";
  const [tasks, setTasks] = useState([]);
  const [meta, setMeta] = useState({ delay_reasons: [] });
  const [scanOpen, setScanOpen] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [wfTask, setWfTask] = useState(null);
  const [editTask, setEditTask] = useState(null);
  const [historyTask, setHistoryTask] = useState(null);
  const [reasonAction, setReasonAction] = useState(null);
  const [filters, setFilters] = useState({ status: "", task_type: "", bill_no: "" });

  const adminAction = async (action, task, reason = null) => {
    if (action === "edit") return setEditTask(task);
    if (action === "history") return setHistoryTask(task);
    const needsReason = ["hold", "cancel", "reopen", "void"].includes(action);
    if (needsReason && !reason) {
      setReasonAction({ action, task });
      return;
    }
    try {
      await api.post(`/tasks/${task.task_id}/${action}`, reason ? { reason } : {});
      toast.success(`Task ${action} successful`);
      setReasonAction(null);
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : `${action} failed`);
    }
  };
  const [completeTask, setCompleteTask] = useState(null);
  const [completeForm, setCompleteForm] = useState({ quantity: "", cartons: "", delay_reason: "", remarks: "", bin: "" });
  const [completeError, setCompleteError] = useState(null);
  const [binOptions, setBinOptions] = useState([]);
  const [pickAllocs, setPickAllocs] = useState([]);
  const [problemTask, setProblemTask] = useState(null);
  const [problemForm, setProblemForm] = useState({ problem: "", delay_reason: "" });
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [assignSel, setAssignSel] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [bills, setBills] = useState([]);
  const [users, setUsers] = useState([]);
  const [assignForm, setAssignForm] = useState({ type: "picking", bill_no: "", assigned_to: [], quantity: "", deadline_minutes: "", location: "", department: "", assigned_user_id: "" });
  const [departments, setDepartments] = useState([]);
  const canManage = ["admin", "warehouse_manager", "supervisor"].includes(user?.role);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (isWorker) params.set("mine", "true");
    if (!isWorker) {
      if (filters.status) params.set("status", filters.status);
      if (filters.task_type) params.set("task_type", filters.task_type);
      if (filters.bill_no) params.set("bill_no", filters.bill_no);
    }
    const qs = params.toString();
    try {
      const res = await api.get(`/tasks${qs ? `?${qs}` : ""}`);
      const pending = queuedTaskIds();
      setTasks(res.data.map((t) => (pending.has(t.task_id) ? { ...t, _pending: true } : t)));
    } catch {
      if (!navigator.onLine) toast.warning("Offline — showing last synced task list");
    }
  }, [isWorker, filters]);

  useEffect(() => {
    load();
    api.get("/meta").then((r) => setMeta(r.data)).catch(() => {});
    const t = setInterval(load, 30000);
    window.addEventListener("wipplops:synced", load);
    return () => { clearInterval(t); window.removeEventListener("wipplops:synced", load); };
  }, [load]);

  const act = async (taskId, action, body = {}) => {
    try {
      const r = await api.post(`/tasks/${taskId}/${action}`, body);
      if (r.offlineQueued) {
        toast.warning(`Offline — ${action} saved locally and will sync when back online`);
        setTasks((prev) => prev.map((t) => t.task_id === taskId ? { ...t, status: { start: "in_progress", resume: "in_progress", pause: "paused" }[action] || t.status, start_time: action === "start" ? new Date().toISOString() : t.start_time, _pending: true } : t));
        return;
      }
      toast.success(`Task ${action} recorded`);
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : `Failed to ${action}`);
    }
  };

  const submitComplete = async () => {
    setCompleteError(null);
    let queued = false;
    try {
      if (completeTask.type === "putaway") {
        if (!completeForm.bin) { setCompleteError("Select a destination bin for the putaway"); return; }
        const r = await api.post(`/tasks/${completeTask.task_id}/putaway-complete`, {
          bin: completeForm.bin,
          quantity: completeForm.quantity ? parseInt(completeForm.quantity) : null,
          delay_reason: completeForm.delay_reason || null,
          remarks: completeForm.remarks || null,
        });
        queued = !!r.offlineQueued;
      } else {
        const payload = {
          quantity: completeForm.quantity ? parseInt(completeForm.quantity) : null,
          cartons: completeForm.cartons ? parseInt(completeForm.cartons) : null,
          delay_reason: completeForm.delay_reason || null,
          remarks: completeForm.remarks || null,
        };
        if (completeTask.type === "picking" && completeTask.sku) {
          const allocs = pickAllocs.filter((a) => a.bin && (parseInt(a.qty) || 0) > 0).map((a) => ({ bin: a.bin, qty: parseInt(a.qty) }));
          if (allocs.length) payload.pick_allocations = allocs;
        }
        const r = await api.post(`/tasks/${completeTask.task_id}/complete`, payload);
        queued = !!r.offlineQueued;
      }
      const doneId = completeTask.task_id;
      if (queued) {
        toast.warning("Offline — completion saved locally and will sync when back online");
        setTasks((prev) => prev.map((t) => t.task_id === doneId ? { ...t, status: "completed", _pending: true } : t));
      } else toast.success("Task completed with timestamps");
      setCompleteTask(null);
      setCompleteForm({ quantity: "", cartons: "", delay_reason: "", remarks: "", bin: "" });
      setPickAllocs([]);
      if (!queued) load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.code === "DELAY_REASON_REQUIRED") {
        setCompleteError(`Exceeded standard time: ${Math.round(d.actual_minutes)}m actual vs ${d.standard_minutes}m standard (+${Math.round(d.delay_minutes)}m). A delay reason is required.`);
      } else if (d?.code === "REMARKS_REQUIRED") {
        setCompleteError("Remarks are required when delay reason is 'Other'.");
      } else if (d?.code === "PICK_ALLOCATIONS_REQUIRED") {
        setCompleteError(d.message);
      } else {
        setCompleteError(typeof d === "string" ? d : d?.message || "Failed to complete task");
      }
    }
  };

  const submitProblem = async () => {
    try {
      await api.post(`/tasks/${problemTask.task_id}/problem`, problemForm);
      toast.success("Problem reported to supervisor");
      setProblemTask(null);
      setProblemForm({ problem: "", delay_reason: "" });
    } catch {
      toast.error("Failed to report problem");
    }
  };

  const handleScan = async (code) => {
    setScanOpen(false);
    try {
      const res = await api.get(`/scan/${encodeURIComponent(code)}`);
      setScanResult(res.data);
    } catch {
      toast.error(`No record found for "${code}"`);
    }
  };

  const openAssign = async () => {
    const [b, u, d] = await Promise.all([api.get("/bills"), api.get("/users"), api.get("/rbac/departments").catch(() => ({ data: [] }))]);
    setBills(b.data);
    setDepartments(d.data);
    setUsers(u.data.filter((x) => ["worker", "dispatch"].includes(x.role)));
    setAssignOpen(true);
  };

  const openAssignExisting = async (task) => {
    const u = await api.get("/users");
    const roleMap = {
      qc: ["qc", "worker"],
      loading: ["dispatch", "worker"],
      einvoice: ["accounts"],
      vehicle_checkin: ["dispatch"],
      handover: ["dispatch"],
      customer_communication: ["sales"],
      pod: ["sales"],
      customer_feedback: ["sales"],
    };
    const rolesFor = roleMap[task.type] || ["worker", "dispatch"];
    const roleUsers = u.data.filter((x) => rolesFor.includes(x.role));
    setAssignableUsers(roleUsers.length ? roleUsers : u.data);
    setAssignSel(task.assigned_to || []);
    setAssignTarget(task);
  };

  const submitAssignExisting = async () => {
    try {
      await api.patch(`/tasks/${assignTarget.task_id}/assign`, { assigned_to: assignSel });
      toast.success("Workers assigned");
      setAssignTarget(null);
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Failed to assign");
    }
  };

  const submitAssign = async () => {
    try {
      await api.post("/tasks", {
        ...assignForm,
        bill_no: assignForm.bill_no || null,
        quantity: assignForm.quantity ? parseInt(assignForm.quantity) : null,
        deadline_minutes: assignForm.deadline_minutes ? parseInt(assignForm.deadline_minutes) : null,
      });
      toast.success("Task assigned");
      setAssignOpen(false);
      setAssignForm({ type: "picking", bill_no: "", assigned_to: [], quantity: "", deadline_minutes: "", location: "", department: "", assigned_user_id: "" });
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Failed to assign task");
    }
  };

  const terminalStatuses = ["completed", "cancelled", "voided"];
  const openTasks = tasks.filter((t) => !terminalStatuses.includes(t.status));
  const doneTasks = tasks.filter((t) => terminalStatuses.includes(t.status));

  return (
    <div className={isWorker ? "max-w-lg mx-auto space-y-4" : "space-y-4"} data-testid="tasks-page">
      <SectionTitle
        right={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setScanOpen(true)} data-testid="open-scanner-button">
              <ScanLine className="w-4 h-4 mr-1" /> Scan
            </Button>
            {canManage && (
              <Button onClick={openAssign} className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="assign-task-button">
                <Plus className="w-4 h-4 mr-1" /> Assign Task
              </Button>
            )}
          </div>
        }
      >
        {isWorker ? "My Tasks — Today" : "All Tasks"}
      </SectionTitle>

      {!isWorker && (
        <div className="flex flex-wrap gap-2" data-testid="task-filters">
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
            <SelectTrigger className="w-40 h-9" data-testid="filter-status"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              {["all", "assigned", "in_progress", "paused", "on_hold", "completed", "cancelled", "voided"].map((s) => (
                <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.task_type} onValueChange={(v) => setFilters({ ...filters, task_type: v === "all" ? "" : v })}>
            <SelectTrigger className="w-44 h-9" data-testid="filter-type"><SelectValue placeholder="All types" /></SelectTrigger>
            <SelectContent>
              {["all", ...(meta.task_types || [])].map((t) => (
                <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input className="w-40 h-9" placeholder="Order / Bill no" value={filters.bill_no} onChange={(e) => setFilters({ ...filters, bill_no: e.target.value })} data-testid="filter-bill" />
        </div>
      )}

      <div className="space-y-3">
        {openTasks.map((t) => (
          <TaskCard key={t.task_id} t={t} isWorker={isWorker} canManage={canManage} userId={user?.user_id} userRole={user?.role}
            onNavigateBill={(bn) => navigate(bn.startsWith("SO-") ? `/orders/${bn}` : `/bills/${bn}`)}
            act={act} onAssign={openAssignExisting}
            onComplete={async (task) => {
              if (WORKFLOW_TASK_TYPES.includes(task.type)) { setWfTask(task); return; }
              setCompleteTask(task); setCompleteForm({ quantity: task.quantity?.toString() || "", cartons: task.cartons?.toString() || "", delay_reason: "", remarks: "", bin: "" }); setCompleteError(null);
              if (task.type === "putaway") {
                try {
                  const r = await api.get("/locations");
                  setBinOptions(r.data.filter((l) => !l.sku || l.sku === task.sku));
                } catch { setBinOptions([]); }
              } else setBinOptions([]);
              if (task.type === "picking" && task.sku && task.quantity) {
                try {
                  const r = await api.get(`/stock/suggest-bins?sku=${encodeURIComponent(task.sku)}&qty=${task.quantity}`);
                  setPickAllocs(r.data.allocations.map((a) => ({ bin: a.bin, bin_qty: a.bin_qty, qty: a.suggested_qty.toString() })));
                } catch { setPickAllocs([]); }
              } else setPickAllocs([]);
            }}
            onProblem={setProblemTask} onAdminAction={adminAction} isAdmin={user?.role === "admin"} isManager={["admin", "warehouse_manager"].includes(user?.role)} />
        ))}
        {openTasks.length === 0 && (
          <div className="bg-card border border-border rounded-lg p-8 text-center text-muted-foreground" data-testid="no-open-tasks">
            No open tasks. You're all clear.
          </div>
        )}
      </div>

      {doneTasks.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-lg font-semibold uppercase tracking-tight text-muted-foreground">Completed / Closed</h3>
          {doneTasks.map((t) => (
            <DoneTaskRow key={t.task_id} t={t} isWorker={isWorker} canManage={canManage} isManager={["admin", "warehouse_manager"].includes(user?.role)} act={act} onAdminAction={adminAction} />
          ))}
        </div>
      )}

      <ScanDialogs scanOpen={scanOpen} setScanOpen={setScanOpen} onScan={handleScan}
        scanResult={scanResult} setScanResult={setScanResult} onOpenBill={(bn) => navigate(`/bills/${bn}`)} />

      <WorkflowCompleteDialog task={wfTask} onClose={() => setWfTask(null)} onDone={load} />
      <TaskEditDialog task={editTask} onClose={() => setEditTask(null)} onDone={load} />
      <TaskHistoryDialog task={historyTask} onClose={() => setHistoryTask(null)} />
      <TaskReasonDialog action={reasonAction?.action} task={reasonAction?.task} onClose={() => setReasonAction(null)}
        onSubmit={(reason) => adminAction(reasonAction.action, reasonAction.task, reason)} />

      <TaskDialogs
        meta={meta}
        completeTask={completeTask} setCompleteTask={setCompleteTask} completeForm={completeForm} setCompleteForm={setCompleteForm}
        completeError={completeError} submitComplete={submitComplete}
        binOptions={binOptions} pickAllocs={pickAllocs} setPickAllocs={setPickAllocs}
        problemTask={problemTask} setProblemTask={setProblemTask} problemForm={problemForm} setProblemForm={setProblemForm}
        submitProblem={submitProblem}
        assignTarget={assignTarget} setAssignTarget={setAssignTarget} assignableUsers={assignableUsers}
        assignSel={assignSel} setAssignSel={setAssignSel} submitAssignExisting={submitAssignExisting}
        assignOpen={assignOpen} setAssignOpen={setAssignOpen} assignForm={assignForm} setAssignForm={setAssignForm}
        bills={bills} users={users} departments={departments} submitAssign={submitAssign}
      />
    </div>
  );
}
